import { type Nullable } from "@sunrise/utils";
import clsx from "clsx";
import { type ReactNode, type Ref, forwardRef } from "react";

import { typography } from "@/styles/typography.css";

import * as styles from "./progres-bar.css";

export type ProgramBreak = {
  start: number;
  length?: number;
  isRecording?: boolean;
};

type ProgressBarProps = CommonProps & {
  height?: number;
  radius?: number;
  liveProgress: Nullable<number>;
  replayProgress?: Nullable<number>;
  breaks?: ProgramBreak[];
};
export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  function ProgressBarContent(
    {
      className,
      height,
      radius,
      liveProgress,
      replayProgress,
      breaks,
      "data-testid": dataTestId = "progressBar",
    }: ProgressBarProps,
    ref: Ref<HTMLDivElement>,
  ): ReactNode {
    return (
      <div
        data-testid={dataTestId}
        className={clsx([styles.defaultStyle, typography.h7.bold, className])}
        style={{ height, borderRadius: radius }}
        ref={ref}
      >
        <div
          className={styles.liveProgressStyle}
          style={{ width: `${liveProgress}%`, height, borderRadius: radius }}
          data-testid={`${dataTestId}.liveProgress`}
          data-progress={liveProgress}
        />
        {!!replayProgress && (
          <div
            className={styles.replayProgressStyle}
            style={{
              width: `${replayProgress}%`,
              height,
              borderRadius: radius,
            }}
            data-testid={`${dataTestId}.replayProgress`}
            data-progress={replayProgress}
          />
        )}
        {breaks?.map((br, idx) => (
          <div
            key={`${idx}-${br.start}-${br.length}`}
            className={clsx([
              styles.breakStyle,
              br.isRecording && styles.recordingBreakStyle,
            ])}
            style={{
              width: !br.isRecording ? `${br.length ?? 2}%` : height,
              left: `${br.start}%`,
              height,
            }}
            data-testid={`${dataTestId}.break.${idx}`}
          />
        ))}
      </div>
    );
  },
);
